import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showError, showSuccess } from "@/shared/utils/toast";
import { useMainStore } from "@/stores";
import { getOrganizationByEmail, requestAccess } from "@/api/auth";
import {
  getTargetRoute,
  handleReturnToSignUp,
} from "@/shared/utils/helperFunctions";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { ArrowLeft } from "lucide-react";

const ConfirmationOrgJoin = () => {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [org, setOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useMainStore();

  useEffect(() => {
    const orgFromState = state?.org;
    if (orgFromState) {
      setOrg(orgFromState);
      setIsLoading(false);
      return;
    }
    const fetchOrg = async () => {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await getOrganizationByEmail(user.email);
        setOrg(res.organization);
      } catch {
        setOrg(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrg();
  }, [user?.email, state?.org]);

  useEffect(() => {
    if (!isLoading && !org && user?.email) {
      navigate("/join-organization", { replace: true });
    }
  }, [isLoading, org, user?.email, navigate]);

  const confirmationInfo = org
    ? [
        { title: "Your Email", value: user?.email || "—" },
        { title: "Organization", value: org.name || "—" },
      ]
    : [];

  const handleRequestAccess = async () => {
    if (!user?.email) return;
    setIsSubmitting(true);
    try {
      await requestAccess({
        email: user.email,
        comment: message.trim() || "Please approve my account",
        target_role: "org_admin",
      });
      setUser({
        ...user,
        approvalStatus: "pending",
        hasAccessRequest: true,
        requestTarget: "org",
        orgName: org?.name,
        requestSubmittedAt: new Date().toISOString(),
      });
      navigate("/join-organization/status");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to send request. Please try again.";

      if (/already approved/i.test(msg)) {
        const updated = { ...user, approvalStatus: "approved" };
        setUser(updated);
        showSuccess("Your account is auto approved!");
        setIsSubmitting(false);
        navigate(
          getTargetRoute(updated, {
            appMode: useMainStore.getState().appMode ?? "user",
          }),
        );
        return;
      }

      showError(msg);
      setIsSubmitting(false);
    }
  };

  if (isLoading || !org) {
    return (
      <div className="flex items-center justify-center h-[100dvh] w-full">
        <Loader
          text={isLoading ? "Loading organization..." : "Redirecting..."}
        />
      </div>
    );
  }

  return (
    <div className="bg-[#F9FAFB] flex items-center justify-center h-[100dvh] w-full">
      <div className="flex items-start  gap-8  w-[90%]">
        <div
          className="flex items-center gap-1.5 cursor-pointer"
          onClick={() => handleReturnToSignUp(navigate)}
        >
          <ArrowLeft
            strokeWidth={1.5}
            size={20}
            className="text-secondary cursor-pointer"
          />
          <button className="text-secondary font-normal text-[0.85rem] hover:underline transition-all duration-300 w-fit">
            Return to Login
          </button>
        </div>
        <div className="flex flex-col gap-5 w-[47%] mx-auto max-w-[512px]">
          <div className="border border-gray-200 rounded-2xl p-6 bg-white flex flex-col gap-5 shadow-sm">
            {/* heading */}
            <h1 className="text-gray-800 text-[1.38rem] leading-8 font-normal">
              Request Access to {org?.name || "Organization"}
            </h1>
            {confirmationInfo.map((info, index) => (
              <div className="flex flex-col gap-1" key={index}>
                <h5 className="text-gray-800 font-semibold text-[0.85rem]">
                  {info.title}
                </h5>
                <CommonInput
                  value={info.value}
                  disabled={true}
                  className="bg-gray-100"
                  inputClassName="bg-gray-100 text-[0.9rem] text-gray-800"
                />
              </div>
            ))}
            {/* message to admin */}
            <div className="flex flex-col gap-1.5">
              <label className="text-gray-800 text-[0.85rem] font-semibold">
                Message to Admin (Optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g., Hi Sarah, I'm from the marketing team and would like to join the workspace."
                rows={3}
                maxLength={200}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-[0.85rem] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-secondary resize-none transition-all duration-200"
              />
              <p className="text-gray-400 text-[0.7rem] text-right">
                {message.length}/200
              </p>
            </div>

            {/* admin info */}
            <div className="bg-gray-50 rounded-[10px] p-4 flex flex-col gap-1.5">
              <p className="text-gray-500 text-[0.85rem]">
                Your request will be sent to your organization admin. They will
                receive an email notification and can approve your access from
                their team management settings.
              </p>
            </div>

            <div className="flex gap-3 w-full">
              <SecondaryButton
                title="Back"
                onClick={() => navigate("/join-organization")}
                className="w-full"
              />
              <PrimaryButton
                children="Send Request"
                startContent={false}
                onClick={handleRequestAccess}
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationOrgJoin;
